const destinations = [
  {
    icon: "ri-ship-line",
    title: "The Bahamas &amp; Caribbean",
    description:
      "The Bahamas are Marco Island's closest international neighbor — Bimini is roughly 50 miles across the Gulf Stream, Grand Bahama about 75 miles, and the Abacos roughly 100 miles. Local boaters make these runs regularly for fishing tournaments, island-hopping weekends, and extended cruising. Caribbean cruises from Port Everglades, Port of Miami, and Port Tampa Bay are equally popular, with itineraries to the Eastern Caribbean (St. Thomas, San Juan, the Bahamas), Western Caribbean (Cozumel, Grand Cayman, Jamaica), and Southern Caribbean (Aruba, Curacao, Barbados). The six-month validity rule is critical for both boating and cruising — Bahamas immigration requires it, and cruise lines enforce it at boarding.",
    tags: ["Boating", "Cruises", "Fishing Tournaments"],
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Canada",
    description:
      "Canadian travel is significant for Marco Island's large population of former Midwest and Northeast residents who maintain deep family connections across the border. Summer returns to Toronto, Ottawa, Montreal, and Vancouver are a longstanding tradition, often timed with grandchildren's graduations, weddings, and milestone birthdays. Direct seasonal flights from RSW to Toronto and Montreal make this easier than ever. The weak Canadian dollar has also made cross-border shopping and visiting more attractive for Florida residents with Canadian ties.",
    tags: ["Family Visits", "Summer Travel", "Direct Flights"],
  },
  {
    icon: "ri-landscape-line",
    title: "Europe",
    description:
      "European destinations draw Marco Island's affluent traveler demographic for river cruises along the Rhine, Danube, and Douro; cultural tours of London, Paris, Rome, and Barcelona; and Mediterranean cruises from Rome, Barcelona, and Venice. The Schengen three-month validity rule catches many retirees who assume their passport is fine because it has not expired. Luxury villa rentals in Tuscany, Provence, and the Amalfi Coast are popular among Marco Island's wealthy seasonal residents, and these bookings are typically non-refundable within 60 days of arrival.",
    tags: ["River Cruises", "Luxury Villas", "Culture"],
  },
  {
    icon: "ri-treasure-map-line",
    title: "United Kingdom &amp; Ireland",
    description:
      "The UK and Ireland are popular for heritage travel, golf trips to Scotland's St. Andrews and Ireland's championship links, and London city breaks. The six-month validity requirement is particularly strict for UK entry — we see more passport emergencies from UK-bound travelers than almost any other destination. Marco Island's golf-loving demographic frequently books these trips 9–12 months in advance through tour operators, then discovers passport issues when final travel documents are distributed. The weak pound has made Britain an increasingly attractive destination for budget-conscious luxury travelers.",
    tags: ["Heritage", "Golf", "City Breaks"],
  },
  {
    icon: "ri-sun-line",
    title: "Mexico &amp; Costa Rica",
    description:
      "Cancun, Riviera Maya, Puerto Vallarta, and Cabo San Lucas are top Mexican destinations for Marco Island families and retirees. Winter escapes to all-inclusive resorts are a snowbird staple, while spring break trips draw families with school-age children. Costa Rica's eco-tourism — zip-lining through rainforests, volcano hiking, and Pacific Coast surfing — appeals to Marco Island's active outdoor demographic. The Mexico six-month validity rule applies to all passport holders, and the child passport trap hits hard when parents book that first Cancun trip and discover their 8-year-old's passport expired last year.",
    tags: ["Resorts", "Eco-Tourism", "Winter Escape"],
  },
  {
    icon: "ri-compass-3-line",
    title: "Panama Canal &amp; Central America",
    description:
      "Panama Canal cruises, Costa Rica eco-tours, and Belize diving trips appeal to Marco Island's active retiree and boating community. Panama Canal full-transit cruises are a bucket-list item for many affluent retirees, booking 12–18 months in advance through luxury cruise lines. Belize's barrier reef — the second largest in the world — draws diving enthusiasts from Southwest Florida who compare it favorably to their own local waters. These trips often book well in advance, creating a long window during which passports can expire unnoticed.",
    tags: ["Bucket List", "Diving", "Cruises"],
  },
];

const stats = [
  { value: "60%", label: "Bahamas boating or Caribbean cruise requests" },
  { value: "RSW", label: "Primary airport — 45–50 min from Marco Island" },
  { value: "Nov–Apr", label: "Peak travel season for snowbirds &amp; tournaments" },
  { value: "24 hr", label: "Shortest emergency turnaround handled" },
];

export default function MarcoIslandDestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Popular Destinations
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Marco Island Residents Travel
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            From Bimini fishing tournaments to European river cruises, here is where Marco Island travelers are heading — and the passport rules that matter for each destination.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((d, i) => (
            <div
              key={i}
              className="bg-[#F8F9FB] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4">
                <i className={`${d.icon} text-[#F6C344] text-lg`}></i>
              </div>
              <h3 className="font-serif text-base font-bold text-[#1A365D] mb-3">{d.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{d.description}</p>
              <div className="flex flex-wrap gap-2">
                {d.tags.map((tag, j) => (
                  <span
                    key={j}
                    className="inline-block bg-white text-[#1A365D] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="bg-[#1A365D] rounded-lg p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-[#F6C344] font-bold text-2xl md:text-3xl mb-1">{s.value}</div>
                <div className="text-white/70 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}